import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Error Boundary
import ErrorBoundary from './components/ErrorBoundary';

// Components
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import JobsPage from './pages/JobsPage';
import BartendersPage from './pages/BartendersPage';
import OwnerBrowseBartendersPage from './pages/OwnerBrowseBartendersPage';
import OwnerLandingPage from './pages/OwnerLandingPage';
import BartenderDetailPage from './pages/BartenderDetailPage';
import BarOwnerProfilePage from './pages/BarOwnerProfilePage';
import ProfilePage from './pages/ProfilePage';
import MessagesPage from './pages/MessagesPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PremiumPage from './pages/PremiumPage';
import AdminDashboard from './pages/AdminDashboard';
import HealthUI from './pages/HealthUI';
import BartenderJobsYoungstownPage from './pages/BartenderJobsYoungstownPage';
import SavedBartendersPage from './pages/SavedBartendersPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import RegisterBartendersPage from './pages/RegisterBartendersPage';
import FAQPage from './pages/FAQPage';
import BartenderJobsPage from './pages/BartenderJobsPage';
import BartenderJobsAustinPage from './pages/BartenderJobsAustinPage';
import BartenderJobsMiamiPage from './pages/BartenderJobsMiamiPage';
import BartenderJobsChicagoPage from './pages/BartenderJobsChicagoPage';
import BartenderJobsNashvillePage from './pages/BartenderJobsNashvillePage';
import BartenderJobsLasVegasPage from './pages/BartenderJobsLasVegasPage';
import BartenderJobsNearMePage from './pages/BartenderJobsNearMePage';
import PartTimeBartenderJobsPage from './pages/PartTimeBartenderJobsPage';
import EventBartenderJobsPage from './pages/EventBartenderJobsPage';
import BartenderAppPage from './pages/BartenderAppPage';
import InstallPrompt from './components/InstallPrompt';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const envBackendUrl = process.env.REACT_APP_BACKEND_URL;

if (!envBackendUrl) {
  throw new Error('REACT_APP_BACKEND_URL is not configured');
}

let BACKEND_URL = envBackendUrl;

if (runtimeOrigin) {
  try {
    const runtimeHost = new URL(runtimeOrigin).host;
    const envHost = new URL(envBackendUrl).host;

    if (runtimeHost !== envHost) {
      BACKEND_URL = runtimeOrigin;
    }
  } catch (error) {
    console.warn('Failed to parse backend URL, falling back to REACT_APP_BACKEND_URL', error);
    BACKEND_URL = envBackendUrl;
  }
}

const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0B0B0F',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1rem',
            fontFamily: 'Barlow Condensed, sans-serif',
            letterSpacing: '0.1em'
          }}>
            BARSCONNECT
          </div>
          <div style={{ fontSize: '1rem', opacity: 0.7 }}>Loading...</div>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0B0B0F',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1rem',
            fontFamily: 'Barlow Condensed, sans-serif',
            letterSpacing: '0.1em'
          }}>
            BARSCONNECT
          </div>
          <div style={{ fontSize: '1rem', opacity: 0.7 }}>Loading...</div>
        </div>
      </div>
    );
  }
  
  return !user ? children : <Navigate to="/dashboard" replace />;
};

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  const hideNavbarRoutes = ['/bartender-jobs-youngstown-oh'];
  const shouldShowNavbar = user && !hideNavbarRoutes.includes(location.pathname);

  useEffect(() => {
    const isVisualEditMode = /[?&](emergent_visual_edit|visual_edit)=true(?:&|$)/.test(window.location.search);
    if (isVisualEditMode) return;

    const hasOpenRadixModal = () => {
      const selectors = [
        '[role="dialog"][data-state="open"]',
        '[data-state="open"][data-radix-dialog-content]',
        '[data-state="open"][data-radix-alert-dialog-content]',
        '[data-state="open"][data-radix-sheet-content]',
      ];

      return selectors.some((selector) => document.querySelector(selector));
    };

    const healStuckPointerLock = () => {
      const body = document.body;
      if (!body) return;

      const computedPointerEvents = window.getComputedStyle(body).pointerEvents;
      if (computedPointerEvents === 'none' && !hasOpenRadixModal()) {
        body.style.pointerEvents = 'auto';
      }
    };

    healStuckPointerLock();

    let attempts = 0;
    const maxAttempts = 24;
    const intervalId = window.setInterval(() => {
      healStuckPointerLock();
      attempts += 1;

      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [location.pathname, user?.id]);

  return (
    <div className="min-h-screen bg-background">
      {shouldShowNavbar && <Navbar />}
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register/bartenders"
          element={<RegisterBartendersPage />}
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs"
          element={
            <ProtectedRoute>
              <JobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bartenders"
          element={<BartendersPage />}
        />
        <Route
          path="/for-bar-owners"
          element={<OwnerLandingPage />}
        />
        <Route
          path="/owner/browse-bartenders"
          element={
            <ProtectedRoute>
              <OwnerBrowseBartendersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bartenders/:id"
          element={<BartenderDetailPage />}
        />
        <Route
          path="/bars/:ownerId"
          element={
            <ProtectedRoute>
              <BarOwnerProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/saved-bartenders"
          element={
            <ProtectedRoute>
              <SavedBartendersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-settings"
          element={
            <ProtectedRoute>
              <AccountSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <ProtectedRoute>
              <PricingPage />
            </ProtectedRoute>
          }
        />
        {/* Redirect payment routes */}
        <Route 
          path="/checkout" 
          element={<Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/pay" 
          element={<Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/subscribe" 
          element={<Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/payment-success" 
          element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/premium"
          element={
            <ProtectedRoute>
              <PremiumPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        {/* Redirect /admin to /admin/dashboard */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        
        {/* Health Check UI - No Auth Required */}
        <Route path="/health-ui" element={<HealthUI />} />

        {/* Public SEO Landing Page - No Auth Required */}
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/bartender-jobs" element={<BartenderJobsPage />} />
        <Route path="/bartender-jobs-youngstown-oh" element={<BartenderJobsYoungstownPage />} />
        <Route path="/bartender-jobs-austin-tx" element={<BartenderJobsAustinPage />} />
        <Route path="/bartender-jobs-miami-fl" element={<BartenderJobsMiamiPage />} />
        <Route path="/bartender-jobs-chicago-il" element={<BartenderJobsChicagoPage />} />
        <Route path="/bartender-jobs-nashville-tn" element={<BartenderJobsNashvillePage />} />
        <Route path="/bartender-jobs-las-vegas-nv" element={<BartenderJobsLasVegasPage />} />
        <Route path="/bartender-jobs-near-me" element={<BartenderJobsNearMePage />} />
        <Route path="/part-time-bartender-jobs" element={<PartTimeBartenderJobsPage />} />
        <Route path="/event-bartender-jobs" element={<EventBartenderJobsPage />} />
        <Route path="/bartender-app" element={<BartenderAppPage />} />
      </Routes>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
      <InstallPrompt />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
